import { MutationCache } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import {
  extractApiError,
  extractValidationDetails,
} from "../utils/formErrorHandler";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /**
       * The form owns error UX via handleFormErrors (inline 400→field
       * mapping + its own generic toast) — the global handler stays silent.
       * Set via FORM_MUTATION_META so opted-out mutations are grep-able.
       */
      suppressErrorNotification?: boolean;
      /**
       * Domain-specific message shown when the server provides no error
       * body (network failure, bare 5xx). Replaces the per-hook onError
       * blocks whose only job was this fallback copy.
       */
      errorFallback?: string;
    };
  }
}

/**
 * US-081: one error notification for any rejected mutation, declared nowhere.
 *
 * Message priority: field-level validation details → structured server error
 * → meta.errorFallback → extractApiError. Validation details lead because a
 * field-level 400 sets the top-level error to the generic "Validation failed"
 * and puts the real reason in `details` (SCRUM-124) — without this the user
 * sees "Validation failed" with no hint of what to fix. The fallback outranks
 * error.message deliberately — for axios failures error.message is raw
 * transport copy ("Network Error", "Request failed with status code 500"), and
 * the removed per-hook onError blocks always preferred their domain fallback.
 *
 * MutationCache.onError fires BEFORE the rejection reaches a mutateAsync
 * caller's catch (query-core mutation execute), so a form cannot suppress
 * this toast after the fact — form-backed mutations must opt out up front
 * with FORM_MUTATION_META.
 */
function mutationErrorMessage(error: unknown, errorFallback?: string): string {
  const validationDetail = extractValidationDetails(error);
  if (validationDetail) return validationDetail;
  const serverMessage = (error as { response?: { data?: { error?: string } } })
    ?.response?.data?.error;
  if (serverMessage) return serverMessage;
  if (errorFallback) return errorFallback;
  return extractApiError(error);
}

export function createAppMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // SCRUM-148: a mutation short-circuited locally by the read-only
      // assumed-role guard rejects with an axios cancellation and already
      // showed its own toast — don't pile a generic red "Error" on top.
      if (axios.isCancel(error)) return;
      if (mutation.meta?.suppressErrorNotification) return;
      notifications.show({
        title: "Error",
        message: mutationErrorMessage(error, mutation.meta?.errorFallback),
        color: "red",
      });
    },
  });
}
